import type { ToothBasisSeriesName } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type { ToothBasisSeriesCacheEntry } from '../config';
import {
  TOOTH_BASIS_SERIES_CACHE_LIMIT,
  TOOTH_BASIS_SERIES_ORDER,
} from '../config';

const entryByCacheKey = new Map<
  string,
  {
    entry: ToothBasisSeriesCacheEntry;
    projectionKeys?: Record<ToothBasisSeriesName, string>;
  }
>();
const listenersByCacheKey = new Map<
  string,
  Set<(entry: ToothBasisSeriesCacheEntry) => void>
>();

const evictUnobservedEntries = () => {
  const evictableKeys = [...entryByCacheKey.keys()].filter(
    (key) => !listenersByCacheKey.has(key),
  );
  const evictCount = Math.min(
    evictableKeys.length,
    Math.max(0, entryByCacheKey.size - TOOTH_BASIS_SERIES_CACHE_LIMIT),
  );

  evictableKeys
    .slice(0, evictCount)
    .forEach((key) => entryByCacheKey.delete(key));
};

export const getToothBasisSeriesEntry = (cacheKey: string) =>
  entryByCacheKey.get(cacheKey)?.entry ?? null;

export const getReusableToothBasisSeries = (
  projectionKeys: Record<ToothBasisSeriesName, string>,
) =>
  Object.fromEntries(
    TOOTH_BASIS_SERIES_ORDER.map((seriesName) => [
      seriesName,
      [...entryByCacheKey.values()].find(
        (cached) =>
          cached.projectionKeys?.[seriesName] === projectionKeys[seriesName],
      )?.entry,
    ]),
  ) as Partial<Record<ToothBasisSeriesName, ToothBasisSeriesCacheEntry>>;

export const publishToothBasisSeriesEntry = ({
  cacheKey,
  entry,
  projectionKeys,
}: {
  cacheKey: string;
  entry: ToothBasisSeriesCacheEntry;
  projectionKeys?: Record<ToothBasisSeriesName, string>;
}) => {
  entryByCacheKey.delete(cacheKey);
  entryByCacheKey.set(cacheKey, { entry, projectionKeys });

  evictUnobservedEntries();

  listenersByCacheKey.get(cacheKey)?.forEach((listener) => listener(entry));
};

export const clearToothBasisSeriesEntry = (cacheKey: string) => {
  entryByCacheKey.delete(cacheKey);
};

export const clearToothBasisSeriesEntries = () => entryByCacheKey.clear();

export const subscribeToToothBasisSeriesEntry = ({
  cacheKey,
  listener,
}: {
  cacheKey: string;
  listener: (entry: ToothBasisSeriesCacheEntry) => void;
}) => {
  const listeners =
    listenersByCacheKey.get(cacheKey) ??
    new Set<(entry: ToothBasisSeriesCacheEntry) => void>();

  listeners.add(listener);
  listenersByCacheKey.set(cacheKey, listeners);

  return () => {
    listeners.delete(listener);

    if (!listeners.size) {
      listenersByCacheKey.delete(cacheKey);
    }
  };
};
